import { DateRangeFilter } from "@/components/date-filter/DateRangeFilter.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useWorkflowExecutions } from "@/pages/workflows/workflow-execution-hook.tsx";
import {
  useWorkflowExecutionWorkflows,
  useWorkflowExecutionsSearch,
} from "@/queries/workflows/workflow-execution.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ApiWorkflowExecutionFilter,
  ApiWorkflowExecutionListItem,
  ApiWorkflowExecutionStatus,
  getApiWorkflowExecutionStatusTitle,
} from "@/types/workflows/workflow-execution.types.ts";
import {
  CoreFilterType,
  FilterRenderType,
  FilterValueType,
  ListItem,
  Multiselect,
  Page,
  PaginationBlock,
  Table,
  Text,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const WorkflowOverview = () => {
  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canIEdit } = usePermissions();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.workflows.overviewViewed);
  });

  useTitle(t("workflow:executions"), t("workflow:workflows"));

  // Модалка фильтров получает filters при открытии, поэтому процессы грузим заранее и ищем по ним локально.
  const { data: workflows, isLoading: isWorkflowsLoading } = useWorkflowExecutionWorkflows();
  const canViewWorkflowExecutions =
    canIEdit(ApiPermission.workflows_executions) || Boolean(workflows?.length);
  const workflowOptions = (workflows ?? []).map((item) => ({
    id: item.id,
    title: item.title,
  }));
  const statusOptions = Object.values(ApiWorkflowExecutionStatus).map((status) => ({
    id: status,
    title: getApiWorkflowExecutionStatusTitle(status),
  }));

  const {
    filter: { filter, count: filterCount },
    filterButton,
  } = useAppFilter<ApiWorkflowExecutionFilter>({
    filters: [
      library.page_number,
      library.employee_ids,
      {
        name: "statuses",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.custom,
        label: t("common:status"),
        render: (item, { control }) => (
          <Controller
            control={control}
            name={item.name}
            render={({ field }) => (
              <Multiselect
                label={item.label}
                options={statusOptions}
                values={field.value ?? []}
                onChange={field.onChange}
                isClearable
                emptyTitle={t("common:select")}
              />
            )}
          />
        ),
      },
      { ...library.date_from, hideInFilter: true },
      { ...library.date_to, hideInFilter: true },
      {
        name: "date_range",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.string,
        renderType: FilterRenderType.custom,
        label: t("common:period"),
        render: (item, { control }) => (
          <Controller
            control={control}
            name={library.date_from.name}
            render={({ field: fromField }) => (
              <Controller
                control={control}
                name={library.date_to.name}
                render={({ field: toField }) => (
                  <DateRangeFilter
                    title={item.label ?? t("common:period")}
                    from={fromField.value ?? undefined}
                    to={toField.value ?? undefined}
                    onChange={({ from, to }) => {
                      fromField.onChange(from ?? null);
                      toField.onChange(to ?? null);
                    }}
                  />
                )}
              />
            )}
          />
        ),
      },
      {
        name: "workflow_ids",
        type: CoreFilterType.hidden,
        valueType: FilterValueType.numberArray,
        renderType: FilterRenderType.custom,
        label: t("workflow:workflow"),
        render: (item, { control }) => (
          <Controller
            control={control}
            name={item.name}
            render={({ field }) => (
              <Multiselect
                label={item.label}
                options={workflowOptions}
                values={field.value ?? []}
                onChange={field.onChange}
                searchable
                isClearable
                emptyTitle={t("common:select")}
              />
            )}
          />
        ),
      },
    ],
    source: "Workflow executions",
  });

  const {
    data: workflowExecutions,
    isLoading,
    isFetching,
  } = useWorkflowExecutionsSearch({
    filter,
    enabled: !isWorkflowsLoading && canViewWorkflowExecutions,
  });
  const { renderEmployeeName, renderDateAndTime } = useFormatter();

  const { getWorkflowExecutionStatusCell } = useWorkflowExecutions();

  const columns = useMemo<ColumnDef<ApiWorkflowExecutionListItem>[]>(() => {
    const inner: ColumnDef<ApiWorkflowExecutionListItem>[] = [
      {
        header: t("common:title"),
        cell: (row) => {
          const to = `/workflows/executions/${row.row.original.id}`;
          if (row.row.original.employee) {
            const caption = renderEmployeeName(row.row.original.employee);
            return (
              <ListItem
                to={to}
                title={row.row.original.title}
                forceShowAvatar={true}
                behavior={"card"}
                avatarUrl={getEmployeeAvatarUrl(row.row.original.employee, "lg")}
                caption={caption}
              />
            );
          }
          return (
            <ListItem
              to={to}
              title={row.row.original.title}
              forceShowAvatar={true}
              behavior={"card"}
            />
          );
        },
        accessorKey: "title",
        ...ColumnSizes.status,
        meta: { disableRowClick: true },
      },
      {
        header: t("common:status"),
        cell: (row) => getWorkflowExecutionStatusCell(row.row.original),
        accessorKey: "status",
        ...ColumnSizes.status,
      },

      {
        header: t("common:date"),
        cell: (row) => <Text>{renderDateAndTime(row.row.original.datetime_created_at)}</Text>,
        accessorKey: "datetime_created_at",
        ...ColumnSizes.status,
      },
    ];
    return inner;
  }, [workflowExecutions]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.workflows.viewed);
  });

  return (
    <>
      <Page className="h-full">
        <Page.Header title={t("workflow:executions")}>
          {filterButton({ clearable: true })}
        </Page.Header>

        <Page.Content stretch>
          <Page.StickyLayout
            footer={
              <PaginationBlock
                metadata={workflowExecutions?.metadata}
                onPageChange={(page) => {
                  setSearchParams((searchParams) => {
                    searchParams.set("page", page.toString());
                    return searchParams;
                  });
                }}
              />
            }
          >
            <Table
              stickyHeader={true}
              onRowClick={(row) => navigate(`/workflows/executions/${row.original.id}`)}
              data={workflowExecutions?.items || []}
              columns={columns}
              showSkeleton={isLoading}
              isFetching={isLoading || isFetching}
              showNotFoundError={filterCount > 0 && workflowExecutions?.metadata.total_items == 0}
              notFoundComponent={<EmptyStateNothingFound />}
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </>
  );
};
